import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { Location as PatientLocation } from '@redux/device/deviceTypes';
import { clearErrorPostNewDTCPatientAction } from '@redux/person/personActions';
import { getErrorPostNewDTCPatientSelector, isLoadingPostNewDTCPatientSelector } from '@redux/person/personSelector';
import { postCreateDTCPatientThunk } from '@redux/person/personThunks';
import { CreateDTCPatientRequest, GenderType, genderTypeList, PronounType, PronounTypeList, USStateList } from '@redux/person/personTypes';
import { ReduxState } from '@redux/types';
import { RouteNames } from '../../utils/navigationUtils';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { Button } from '../../components/Button/Button';
import {
  CarePlanAutocomplete,
  CarePlanAutocompletePaper,
  CarePlanAutocompletePopper,
  CarePlanAutocompleteTextField as CarePlanTextField
} from '../../components/CarePlanAutocomplete/CarePlanAutocomplete';
import NavTopBarContentWrapper from '../../components/NavTopBarContentWrapper/NavTopBarContentWrapper';
import { TextField } from '../../components/TextField';
import { Text } from '../../components/Typography/Text';
import { customDateTimePickerStyles } from '../../customStyles';
import { convertIsoDateToLocalDate, toUtcMidnightIsoString } from '../../utils/dateInput';
import {
  birthDateAlertMessage,
  emailAlertMessage,
  getCleanPhoneNumber,
  getEnumKey,
  getFormattedPhoneNumber,
  isBirthDateValid,
  isEmailValid,
  isPhoneNumberValid,
  phoneNumberAlertMessage,
  trimObjectValues
} from '../../utils/util';
import style from './NewDTCPatient.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & {};

const emptyDTCPatient: CreateDTCPatientRequest = {
  email: '',
  givenNames: '',
  familyName: '',
  nickname: '',
  phoneNumber: '',
  birthDate: '',
  genderType: null,
  pronounType: null,
  shippingAddress: {} as PatientLocation
};

const NewDTCPatient: React.FC<Props> = ({ isLoadingPostNewDTCPatient, errorPostNewDTCPatient, postCreateDTCPatient, clearErrorPostNewDTCPatient }) => {
  const flags = useFlags();
  const navigate = useNavigate();
  const { showAlertInfo, showAlertWarning } = useAlert();
  const ldClient = useLDClient();
  const showDtcPatientCreation = flags.dtcPatientCreation ?? false;
  const [ldReady, setLdReady] = useState(false);

  const [personFormData, setPersonFormData] = useState<CreateDTCPatientRequest>(emptyDTCPatient);
  const [isDateInvalid, setIsDateInvalid] = useState(false);
  const [errors, setErrors] = useState({
    phoneNumber: '',
    email: '',
    birthDate: ''
  });

  useEffect(() => {
    if (!ldClient) return;
    ldClient.waitForInitialization().then(() => setLdReady(true));
  }, [ldClient]);

  useEffect(() => {
    if (!ldReady) return;
    if (!showDtcPatientCreation) {
      navigate(RouteNames.HOME);
    }
  }, [ldReady, showDtcPatientCreation, navigate]);

  useEffect(() => {
    clearErrorPostNewDTCPatient();
    setErrors({ phoneNumber: '', email: '', birthDate: '' });
    return () => {
      clearErrorPostNewDTCPatient();
    };
  }, []);

  useEffect(() => {
    if (errorPostNewDTCPatient) {
      if (errorPostNewDTCPatient.status === 409) {
        setErrors({ ...errors, email: 'Email is already in use' });
      }
      showAlertWarning({
        message: `Failed to create DTC patient: ${errorPostNewDTCPatient.message}`,
        duration: 15000
      });
    }
  }, [errorPostNewDTCPatient]);

  useEffect(() => {
    if (isLoadingPostNewDTCPatient) {
      setErrors((prev) => ({ ...prev, email: '' }));
    }
  }, [isLoadingPostNewDTCPatient]);

  const isSubmitBtnDisabled =
    isLoadingPostNewDTCPatient ||
    !personFormData.givenNames ||
    !personFormData.familyName ||
    !personFormData.phoneNumber ||
    !personFormData.email ||
    !personFormData.birthDate ||
    isDateInvalid;

  const handlePersonChange = (e) => {
    const { name, value } = e.target;
    handlePersonDataChange(name, value);
  };

  const handleShippingAddressChange = (e) => {
    const { name, value } = e.target;
    handleShippingDataChange(name, value);
  };

  const handleShippingDataChange = (name, value) => {
    setPersonFormData({
      ...personFormData,
      shippingAddress: {
        ...personFormData.shippingAddress,
        [name]: value
      }
    });
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const formattedPhoneNumber = getFormattedPhoneNumber(value);
    handlePersonDataChange('phoneNumber', formattedPhoneNumber);
  };

  const handleBirthDateChange = (date: Date | null) => {
    handlePersonDataChange('birthDate', toUtcMidnightIsoString(date));
  };

  const handleGenderTypeChange = (event, value) => {
    const gender = getEnumKey(GenderType, value);
    handlePersonDataChange('genderType', gender);
  };

  const handlePronounTypeChange = (event, value) => {
    const pronoun = getEnumKey(PronounType, value);
    handlePersonDataChange('pronounType', pronoun);
  };

  const handleStateChange = (event, value) => {
    handleShippingDataChange('state', value);
  };

  const handlePersonDataChange = (name, value) => {
    setPersonFormData({
      ...personFormData,
      [name]: value
    });
  };

  const validateForm = () => {
    const newErrors = {
      phoneNumber: isPhoneNumberValid(personFormData.phoneNumber) ? '' : phoneNumberAlertMessage(personFormData.phoneNumber) || '',
      email: isEmailValid(personFormData.email) ? '' : emailAlertMessage(personFormData.email) || '',
      birthDate: personFormData.birthDate && !isBirthDateValid(personFormData.birthDate) ? birthDateAlertMessage(personFormData.birthDate) || '' : ''
    };

    setErrors(newErrors);

    return Object.values(newErrors).every((error) => error === '');
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      const cleanPhoneNumber = getCleanPhoneNumber(personFormData.phoneNumber);
      try {
        const newDTCPatientData = {
          ...trimObjectValues(personFormData),
          genderType: personFormData.genderType,
          pronounType: personFormData.pronounType,
          phoneNumber: cleanPhoneNumber
        } as unknown as CreateDTCPatientRequest;
        await postCreateDTCPatient(newDTCPatientData);

        showAlertInfo({
          message: 'DTC Patient was successfully created!'
        });
        setPersonFormData(emptyDTCPatient);
      } catch (err) {
        // Error alert is handled by the errorPostNewDTCPatient useEffect
      }
    }
  };

  const birthDateValue = convertIsoDateToLocalDate(personFormData.birthDate);

  if (!ldReady || !showDtcPatientCreation) return null;

  return (
    <NavTopBarContentWrapper centerAlignContent headerText="Create New DTC Patient">
      <form onSubmit={handleFormSubmit} className={style.mainContainer}>
        <div className={style.patientInfoContainer}>
          <Text variant="xl">Patient info</Text>
          <br />
          <div className={style.subContainer}>
            <div className={style.formRow}>
              <TextField
                isMandatory
                name="givenNames"
                value={personFormData.givenNames}
                onChange={handlePersonChange}
                label="First name"
                placeholder="First name"
                autoComplete="firstName"
              />
              <TextField
                isMandatory
                name="familyName"
                value={personFormData.familyName}
                onChange={handlePersonChange}
                label="Last name"
                placeholder="Last name"
                autoComplete="lastName"
              />
            </div>
            <div className={style.formRow}>
              <TextField
                isMandatory
                name="email"
                value={personFormData.email}
                onChange={handlePersonChange}
                label="Email address"
                placeholder="Email address"
                autoComplete="email"
                error={errors.email}
              />
              <TextField
                isMandatory
                name="phoneNumber"
                value={personFormData.phoneNumber}
                onChange={handlePhoneNumberChange}
                label="Mobile number"
                placeholder="+1 (555) 000-0000"
                autoComplete="phoneNumber"
                error={errors.phoneNumber}
              />
            </div>
            <div className={style.formRow}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <div className={style.subContainerEvenDistribution}>
                  <Text variant="sm" className={style.labelSelect}>
                    Date of birth<span> *</span>
                  </Text>
                  <DatePicker
                    value={birthDateValue}
                    onChange={(newValue) => handleBirthDateChange(newValue)}
                    disableFuture
                    maxDate={new Date()}
                    onError={(reason) => setIsDateInvalid(!!reason)}
                    sx={customDateTimePickerStyles}
                  />
                </div>
              </LocalizationProvider>
            </div>
            <div className={style.formRow}>
              <TextField name="nickname" value={personFormData.nickname ?? ''} onChange={handlePersonChange} label="Nickname" placeholder="Nickname" autoComplete="nickname" />
            </div>
            <div className={style.formRow}>
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  Gender
                </Text>
                <CarePlanAutocomplete
                  options={genderTypeList}
                  value={personFormData.genderType || null}
                  getOptionLabel={(genderType) => GenderType[genderType as GenderType]}
                  onChange={handleGenderTypeChange}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={option as GenderType}>
                      <div>{option as GenderType}</div>
                    </li>
                  )}
                />
              </div>
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  Pronouns
                </Text>
                <CarePlanAutocomplete
                  options={PronounTypeList}
                  value={personFormData.pronounType || null}
                  getOptionLabel={(pronounType) => PronounType[pronounType as PronounType]}
                  onChange={handlePronounTypeChange}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={option as PronounType}>
                      <div>{option as PronounType}</div>
                    </li>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={style.addressContainer}>
          <Text variant="xl">Address</Text>
          <br />
          <div className={style.subContainer}>
            <div className={style.formRow}>
              <TextField
                name="street"
                className={style.street}
                value={personFormData.shippingAddress?.street ?? ''}
                onChange={handleShippingAddressChange}
                label="Address line 1"
                placeholder="Street address or P.O. box"
                autoComplete="street"
              />
              <TextField
                name="postalCode"
                className={style.postalCode}
                value={personFormData.shippingAddress?.postalCode ?? ''}
                onChange={handleShippingAddressChange}
                label="ZIP code"
                placeholder="ZIP code"
                autoComplete="postalCode"
              />
            </div>
            <div className={style.formRow}>
              <TextField
                name="streetSecondLine"
                value={personFormData.shippingAddress?.streetSecondLine ?? ''}
                onChange={handleShippingAddressChange}
                label="Address line 2"
                placeholder="Apt, suite, unit, building, floor"
                autoComplete="streetSecondLine"
              />
            </div>
            <div className={style.formRow}>
              <div className={style.subContainerEvenDistribution}>
                <TextField
                  name="city"
                  value={personFormData.shippingAddress?.city ?? ''}
                  onChange={handleShippingAddressChange}
                  label="City"
                  placeholder="City"
                  autoComplete="city"
                />
              </div>
              <div className={style.subContainerEvenDistribution}>
                <Text variant="sm" className={style.labelSelect}>
                  State
                </Text>
                <CarePlanAutocomplete
                  options={USStateList}
                  value={personFormData.shippingAddress?.state ?? ''}
                  onChange={handleStateChange}
                  PaperComponent={CarePlanAutocompletePaper}
                  PopperComponent={CarePlanAutocompletePopper}
                  renderInput={(params) => <CarePlanTextField {...params} placeholder="Select" />}
                  renderOption={(props, option, state) => (
                    <li {...props} key={option as string}>
                      <div>{option as string}</div>
                    </li>
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={style.submitButtonContainer}>
          <div>
            <Button className={style.buttonCancel} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSubmitBtnDisabled} className={style.button}>
              Submit
            </Button>
          </div>
        </div>
      </form>
    </NavTopBarContentWrapper>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    isLoadingPostNewDTCPatient: isLoadingPostNewDTCPatientSelector(state),
    errorPostNewDTCPatient: getErrorPostNewDTCPatientSelector(state)
  }),
  {
    postCreateDTCPatient: postCreateDTCPatientThunk,
    clearErrorPostNewDTCPatient: clearErrorPostNewDTCPatientAction
  }
);

export default compose(connectRedux)(NewDTCPatient);
